<template>
  <main class="consumption-page">
    <section class="page-head">
      <div><span class="eyebrow">POINTS BILLING</span><h1>积分消费明细</h1><p>这里只展示当前登录账户的记录。云资源每 5 分钟结算一次，资源释放时完成最后一次结算。</p></div>
      <el-button @click="$router.push('/recharge-points')">返回积分账户</el-button>
    </section>

    <section class="query-card">
      <div class="quick-ranges">
        <button v-for="item in quickRanges" :key="item.key" type="button" :class="{ active: activeRange === item.key }" @click="selectQuickRange(item.key)">{{ item.label }}</button>
      </div>
      <div class="date-query">
        <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" format="YYYY-MM-DD" value-format="YYYY-MM-DD" :clearable="false" :disabled-date="disableFutureDate" @change="activeRange = ''" />
        <el-button type="primary" :loading="loading" @click="search">查询</el-button>
      </div>
    </section>

    <section class="summary-grid">
      <article><span>查询区间</span><strong>{{ displayRange }}</strong></article>
      <article><span>消费积分</span><strong>{{ formatPoints(summary.total_points) }}</strong></article>
      <article><span>计费时长</span><strong>{{ summary.total_minutes }} <small>分钟</small></strong></article>
      <article><span>记录数量</span><strong>{{ pagination.total }} <small>条</small></strong></article>
    </section>

    <section class="records-card">
      <el-table v-loading="loading" :data="records" stripe empty-text="当前日期范围内暂无消费记录">
        <el-table-column prop="vendor" label="厂商" width="100"><template #default="scope">{{ vendorName(scope.row.vendor) }}</template></el-table-column>
        <el-table-column prop="product" label="资源" width="100"><template #default="scope">{{ productName(scope.row.product) }}</template></el-table-column>
        <el-table-column prop="spec" label="规格" min-width="105" />
        <el-table-column prop="resource_count" label="数量" width="75" align="center" />
        <el-table-column prop="points_rate_per_hour" label="积分/小时" width="110" align="right"><template #default="scope">{{ formatPoints(scope.row.points_rate_per_hour) }}</template></el-table-column>
        <el-table-column prop="start_time" label="计费开始" width="170" />
        <el-table-column prop="end_time" label="计费结束" width="170" />
        <el-table-column prop="duration_minutes" label="时长" width="85" align="right"><template #default="scope">{{ scope.row.duration_minutes }} 分钟</template></el-table-column>
        <el-table-column prop="points_deducted" label="消费积分" width="105" align="right"><template #default="scope"><strong class="points-value">-{{ formatPoints(scope.row.points_deducted) }}</strong></template></el-table-column>
      </el-table>
      <div v-if="pagination.total > pagination.page_size" class="pagination-row">
        <el-pagination v-model:current-page="pagination.page" :page-size="pagination.page_size" :total="pagination.total" layout="prev, pager, next, total" @current-change="loadConsumption" />
      </div>
    </section>

    <p class="billing-note">说明：虚拟机计算、系统盘和商业镜像授权以 5 分钟为最小计费单元，不足 5 分钟按 5 分钟收取；公网流量按实际用量计费。</p>
  </main>
</template>

<script>
import axios from "axios";
import { ElMessage } from "element-plus";

function localDate(offsetDays = 0) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + offsetDays);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default {
  name: "PointsConsumption",
  data() {
    return {
      loading: false,
      activeRange: "today",
      dateRange: [localDate(), localDate()],
      quickRanges: [
        { key: "today", label: "当天" },
        { key: "three_days", label: "最近 3 天" },
        { key: "week", label: "最近一周" },
      ],
      records: [],
      summary: { total_points: 0, total_minutes: 0 },
      pagination: { page: 1, page_size: 20, total: 0 },
    };
  },
  computed: {
    displayRange() {
      if (!this.dateRange?.length) return "--";
      return this.dateRange[0] === this.dateRange[1] ? this.dateRange[0] : `${this.dateRange[0]} 至 ${this.dateRange[1]}`;
    },
  },
  mounted() { this.loadConsumption(); },
  methods: {
    disableFutureDate(date) { return date.getTime() > Date.now(); },
    selectQuickRange(key) {
      const startOffsets = { today: 0, three_days: -2, week: -6 };
      this.activeRange = key;
      this.dateRange = [localDate(startOffsets[key]), localDate()];
      this.search();
    },
    search() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        ElMessage.warning("请选择查询日期范围");
        return;
      }
      this.pagination.page = 1;
      this.loadConsumption();
    },
    async loadConsumption() {
      this.loading = true;
      try {
        const response = await axios.get("/api/points/consumption_records", {
          params: {
            start_date: this.dateRange[0], end_date: this.dateRange[1],
            page: this.pagination.page, page_size: this.pagination.page_size,
          },
        });
        this.records = response.data.records || [];
        this.summary = response.data.summary || { total_points: 0, total_minutes: 0 };
        this.pagination = { ...this.pagination, ...(response.data.pagination || {}) };
      } catch (error) {
        this.records = [];
        ElMessage.error(error.response?.data?.error || "获取消费记录失败");
      } finally { this.loading = false; }
    },
    formatPoints(value) {
      const number = Number(value || 0);
      return number.toLocaleString("zh-CN", { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    },
    vendorName(value) { return { aliyun: "阿里云", internal: "平台" }[value] || value; },
    productName(value) { return { vm: "云服务器", disk: "云盘", egress: "出网流量", "vm-task": "实验环境" }[value] || value; },
  },
};
</script>

<style scoped>
.consumption-page { min-height: calc(100vh - 70px); padding: 34px 24px 64px; background: #f4f7fb; color: #203348; text-align: left; }.page-head, .query-card, .summary-grid, .records-card, .billing-note { width: min(1180px, 100%); margin-inline: auto; }.page-head { display: flex; justify-content: space-between; align-items: center; gap: 24px; }.eyebrow { color: #2878d0; font-size: 11px; font-weight: 800; letter-spacing: .12em; }.page-head h1 { margin: 6px 0 8px; font-size: 30px; }.page-head p { margin: 0; color: #6b7b8e; }
.query-card { display: flex; justify-content: space-between; align-items: center; gap: 18px; margin-top: 25px; padding: 17px 19px; border: 1px solid #e0e8f0; border-radius: 13px; background: #fff; box-shadow: 0 7px 24px rgba(34,64,98,.04); }.quick-ranges { display: flex; gap: 7px; }.quick-ranges button { padding: 8px 13px; border: 1px solid #d7e1eb; border-radius: 8px; background: #fff; color: #627488; cursor: pointer; }.quick-ranges button:hover, .quick-ranges button.active { border-color: #2878d0; background: #edf5fd; color: #2878d0; }.date-query { display: flex; gap: 9px; }
.summary-grid { display: grid; grid-template-columns: 1.4fr repeat(3,1fr); gap: 12px; margin-top: 14px; }.summary-grid article { display: grid; gap: 7px; padding: 16px 18px; border: 1px solid #e1e8ef; border-radius: 11px; background: #fff; }.summary-grid span { color: #7d8b9a; font-size: 12px; }.summary-grid strong { color: #2b4055; font-size: 18px; }.summary-grid small { color: #7c8a99; font-size: 12px; font-weight: 500; }
.records-card { overflow: hidden; margin-top: 14px; padding: 10px 18px 16px; border: 1px solid #e0e8f0; border-radius: 13px; background: #fff; box-shadow: 0 7px 24px rgba(34,64,98,.04); }.points-value { color: #d05c58; }.pagination-row { display: flex; justify-content: flex-end; padding-top: 16px; }.billing-note { box-sizing: border-box; margin-top: 12px; padding: 12px 15px; border-radius: 8px; background: #edf5fd; color: #60758b; font-size: 13px; }
@media (max-width: 820px) { .consumption-page { padding: 24px 12px 48px; }.page-head, .query-card { align-items: stretch; flex-direction: column; }.date-query { flex-wrap: wrap; }.date-query :deep(.el-date-editor) { width: 100%; }.date-query .el-button { flex: 1; }.summary-grid { grid-template-columns: repeat(2,1fr); }.quick-ranges button { flex: 1; }.quick-ranges { width: 100%; }.records-card { padding-inline: 10px; } }
@media (max-width: 480px) { .summary-grid { grid-template-columns: 1fr; } }
</style>
